import { useState } from "react";
import { Pencil, Plus, X } from "lucide-react";
import {
  AdminShopOptionType,
  createShopOptionType,
  deleteShopOptionType,
  reorderShopOptionTypes,
  updateShopOptionType,
} from "../../../api/shop";
import { Alert, Button, Card, ColorInput, Dialog, EmptyState, Field, IconButton, Input, Switch, Tag, useDialogs } from "../../../ui";
import { codeFromName, normalizeCode } from "../shared/codes";
import { GuardedDelete } from "../shared/GuardedDelete";
import { ReorderControls } from "../shared/ReorderControls";
import { moveItem } from "../shared/reorder";
import { useLatched } from "../shared/useLatched";
import { useDragReorder, usePersistedOrder } from "../shared/useReorder";
import { optionTypeDeleteBlock, plural, usedByText } from "./catalogGuards";
import { OptionTypeForm, ValueRow, newValueRow, normalizeHex, optionTypeRequest, validateOptionType, valueRowsFrom } from "./catalogForms";
import type { CatalogTabProps } from "./types";

const EMPTY: OptionTypeForm = { name: "", hasSwatch: false, values: [] };
/** Values previewed in a type's row before "+N more" */
const PREVIEW_COUNT = 8;
const HEX6 = /^#[0-9a-f]{6}$/i;

const VariantsTab = ({ catalog, onChanged, showError, showSuccess }: CatalogTabProps) => {
  const { confirm } = useDialogs();
  // A saved order is read back into the page's catalog, so it holds after switching tabs
  const order = usePersistedOrder(
    catalog.optionTypes,
    (t) => t.id,
    reorderShopOptionTypes,
    (err) => showError(err, "Failed to reorder variant types"),
    () => void onChanged()
  );
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<AdminShopOptionType | null>(null);
  const [form, setForm] = useState<OptionTypeForm>(EMPTY);
  const [saving, setSaving] = useState(false);
  const [attempted, setAttempted] = useState(false);
  const title = useLatched(open, editing ? "Edit Variant Type" : "Add Variant Type");
  const saveLabel = useLatched(open, editing ? "Update" : "Add");
  const problem = validateOptionType(form);

  // The values reorder inside the drawer; their order is saved with the type
  const valuesDrag = useDragReorder(
    (from, to) => setForm((f) => ({ ...f, values: moveItem(f.values, from, to) })),
    () => undefined
  );
  const stepValue = (index: number, direction: -1 | 1) =>
    setForm((f) => ({ ...f, values: moveItem(f.values, index, index + direction) }));
  const updateValue = (key: string, patch: (row: ValueRow) => Partial<ValueRow>) =>
    setForm((f) => ({ ...f, values: f.values.map((row) => (row.key === key ? { ...row, ...patch(row) } : row)) }));
  const removeValue = (key: string) => setForm((f) => ({ ...f, values: f.values.filter((row) => row.key !== key) }));
  const addValue = () => setForm((f) => ({ ...f, values: [...f.values, newValueRow()] }));

  const openAdd = () => {
    setEditing(null);
    setForm({ ...EMPTY, values: [newValueRow()] });
    setAttempted(false);
    setOpen(true);
  };

  const openEdit = (type: AdminShopOptionType) => {
    setEditing(type);
    setForm({ name: type.name, hasSwatch: type.hasSwatch, values: valueRowsFrom(type) });
    setAttempted(false);
    setOpen(true);
  };

  const close = () => {
    if (!saving) setOpen(false);
  };

  const handleSave = async () => {
    setAttempted(true);
    if (problem) return;
    setSaving(true);
    try {
      const body = optionTypeRequest(form);
      if (editing) {
        await updateShopOptionType(editing.id, body);
        showSuccess("Variant type updated successfully");
      } else {
        await createShopOptionType(body);
        showSuccess("Variant type added successfully");
      }
      setOpen(false);
      await onChanged();
    } catch (err: any) {
      showError(err, "Failed to save variant type");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (type: AdminShopOptionType) => {
    const ok = await confirm({
      title: "Delete Variant Type",
      tone: "danger",
      confirmLabel: "Delete",
      message: (
        <>
          Are you sure you want to delete "<strong>{type.name}</strong>"?
        </>
      ),
    });
    if (!ok) return;
    try {
      await deleteShopOptionType(type.id);
      showSuccess("Variant type deleted successfully");
      await onChanged();
    } catch (err: any) {
      showError(err, "Failed to delete variant type");
    }
  };

  // The colours already in this type come first in the picker
  const usedSwatches = form.values.map((row) => normalizeHex(row.swatchHex)).filter((hex) => HEX6.test(hex));

  return (
    <>
      <Card
        padding="none"
        title="Variants"
        subtitle="Variant types and their values (Colour → Black, White…). Products pick values from these."
        actions={
          <Button size="sm" icon={<Plus size={16} />} onClick={openAdd}>
            Add Variant Type
          </Button>
        }
      >
        {order.list.length === 0 ? (
          <EmptyState compact title="No variant types yet." description="Add Colour (with swatches), Material…" />
        ) : (
          <ul className="catalog-list">
            {order.list.map((type, index) => (
              <li
                key={type.id}
                className={`catalog-row${order.dragIndex === index ? " reorder-dragging" : ""}`}
                {...order.rowProps(index)}
              >
                <ReorderControls
                  index={index}
                  count={order.list.length}
                  onStep={order.step}
                  disabled={order.saving}
                  handleProps={order.handleProps(index)}
                />
                <div className="catalog-row__main">
                  <span className="catalog-row__name">{type.name}</span>
                  {type.hasSwatch && <Tag tone="info">Shows colour swatches</Tag>}
                  <span className="catalog-row__sub">
                    {type.values.length === 0 ? (
                      "No values yet"
                    ) : (
                      <span className="swatch-dots">
                        {type.values.slice(0, PREVIEW_COUNT).map((v) => (
                          <span key={v.id} className="value-chip">
                            {type.hasSwatch && (
                              <span className="swatch-dot" style={{ background: v.swatchHex || "transparent" }} aria-hidden="true" />
                            )}
                            {v.label}
                          </span>
                        ))}
                        {type.values.length > PREVIEW_COUNT && (
                          <span className="muted-text">+{type.values.length - PREVIEW_COUNT} more</span>
                        )}
                      </span>
                    )}
                  </span>
                </div>
                <span className="catalog-row__meta">{plural(type.values.length, "value", "values")}</span>
                <div className="catalog-row__actions">
                  <IconButton label="Edit" icon={<Pencil size={16} />} onClick={() => openEdit(type)} />
                  <GuardedDelete blockedReason={optionTypeDeleteBlock(type)} onDelete={() => handleDelete(type)} />
                </div>
              </li>
            ))}
          </ul>
        )}
      </Card>

      <Dialog
        open={open}
        onClose={close}
        title={title}
        size="lg"
        busy={saving}
        closeOnBackdrop={false}
        bodyClassName="catalog-form"
        footer={
          <>
            <Button variant="ghost" onClick={close} disabled={saving}>
              Cancel
            </Button>
            <Button onClick={handleSave} loading={saving} disabled={!form.name.trim()}>
              {saveLabel}
            </Button>
          </>
        }
      >
        <Field label="Name" required hint="e.g. Colour, Material">
          {(id) => (
            <Input
              id={id}
              value={form.name}
              maxLength={50}
              placeholder="e.g. Colour"
              onChange={(e) => {
                const name = e.target.value;
                setForm((f) => ({ ...f, name }));
              }}
            />
          )}
        </Field>

        <Switch
          checked={form.hasSwatch}
          onChange={(hasSwatch) => setForm((f) => ({ ...f, hasSwatch }))}
          label="Shows colour swatches"
          description="Values show as colour dots in the shop; each value needs a colour"
        />

        <Field label="Values" hint="In the order the shop lists them. A value that a product uses can't be removed.">
          <div className="value-rows">
            {form.values.length > 0 && (
              <div className={`value-row value-row-head${form.hasSwatch ? " has-swatch" : ""}`} aria-hidden="true">
                <span className="value-row__order" />
                <span className="value-row__label">Label</span>
                <span className="value-row__code">Code</span>
                {form.hasSwatch && <span className="value-row__swatch">Colour</span>}
                <span className="value-row__remove" />
              </div>
            )}
            {form.values.map((row, index) => (
              <div
                key={row.key}
                className={`value-row${form.hasSwatch ? " has-swatch" : ""}${valuesDrag.dragIndex === index ? " reorder-dragging" : ""}`}
                {...valuesDrag.rowProps(index)}
              >
                <div className="value-row__order">
                  <ReorderControls
                    index={index}
                    count={form.values.length}
                    onStep={stepValue}
                    handleProps={valuesDrag.handleProps(index)}
                  />
                </div>
                <div className="value-row__label">
                  <Input
                    aria-label="Label"
                    value={row.label}
                    maxLength={50}
                    placeholder="e.g. Black"
                    onChange={(e) => {
                      const label = e.target.value;
                      updateValue(row.key, (r) => ({ label, code: r.codeTouched ? r.code : codeFromName(label) }));
                    }}
                  />
                </div>
                <div className="value-row__code">
                  <Input
                    aria-label="Code"
                    value={row.code}
                    maxLength={6}
                    placeholder="BLK"
                    onChange={(e) => {
                      const code = normalizeCode(e.target.value);
                      updateValue(row.key, () => ({ code, codeTouched: true }));
                    }}
                  />
                </div>
                {form.hasSwatch && (
                  <div className="value-row__swatch">
                    <ColorInput
                      aria-label={`Colour of ${row.label || "this value"}`}
                      value={row.swatchHex}
                      presets={usedSwatches}
                      onChange={(e) => {
                        const swatchHex = e.target.value;
                        updateValue(row.key, () => ({ swatchHex }));
                      }}
                    />
                  </div>
                )}
                <div className="value-row__remove">
                  <GuardedDelete
                    size="sm"
                    label="Remove value"
                    icon={<X size={16} />}
                    blockedReason={usedByText(row.productCount)}
                    onDelete={() => removeValue(row.key)}
                  />
                </div>
              </div>
            ))}
            <Button variant="tint" size="sm" icon={<Plus size={14} />} className="add-value-btn" onClick={addValue}>
              Add value
            </Button>
          </div>
        </Field>

        {attempted && problem && <Alert tone="danger">{problem}</Alert>}
      </Dialog>
    </>
  );
};

export default VariantsTab;
